import { useNavigate } from "react-router-dom";

// Epoch seconds -> "Oct 30, 2025" — the SAME format the player hero uses
// for DOB/"Contract until" (PlayerHeroBanner.formatDate), so both profiles
// read identically (Marin 2026-08-21: „dezenter wie bei den Spielern", with
// the exact dates the API delivers).
const formatFullDate = (epochSeconds) => {
  if (!epochSeconds || !Number.isFinite(Number(epochSeconds))) return null;
  return new Date(Number(epochSeconds) * 1000).toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
};

const CoachHeroBanner = ({
  coach,
  className = "",
  isOnClickNavigate = false,
}) => {
  const navigate = useNavigate();

  // Get coach image - prefer logo, fallback to profile_picture_url
  const coachImage = coach?.logo || coach?.profile_picture_url || coach?.image_path || null;
  
  // Get coach name - prefer name, fallback to display_name
  const coachName = coach?.name || coach?.display_name || "Coach Name";
  
  // Get age - prefer direct age
  const getCoachAge = () => {
    if (coach?.age && coach.age > 0) {
      return coach.age;
    }
    return null;
  };

  const coachAge = getCoachAge();

  // Get team info
  const getTeamInfo = () => {
    if (coach?.team) {
      return {
        name: coach.team.name || "Current Team",
        image: coach.team.logo || coach.team.image_path || null,
      };
    }
    return {
      name: "-",
      image: null,
    };
  };

  const teamInfo = getTeamInfo();

  // Exact dates from the TheSports epochs (BE PR #442) as quiet label rows
  // next to Position/Age — never invented, absent rows render nothing.
  const joinedText = formatFullDate(coach?.joined);
  const untilText = formatFullDate(coach?.contract_until);

  // Get nationality info
  const nationality = coach?.nationality || null;

  return (
    <div
      onClick={() => {
        if (isOnClickNavigate) {
          navigate(`/search/coach/${coach?.id}`);
        }
      }}
      className={`bg-gradient-to-r from-[#1A212F] to-[#374DF5] flex flex-col md:flex-row md:items-center rounded-xl text-white relative overflow-hidden min-h-[200px] ${isOnClickNavigate ? 'cursor-pointer' : ''} ${className}`}
    >
      {/* Main Content */}
      <div className="w-full mx-auto px-6 md:px-10 lg:px-14 py-6 md:py-8 flex items-center relative z-10">
        <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-start md:items-center w-full">
          {/* Left Section - Image and Basic Info */}
          <div className="flex gap-4 md:gap-6 items-center">
            {/* Coach Image */}
            <div className="w-[80px] h-[80px] md:w-[100px] md:h-[100px] rounded-lg overflow-hidden bg-gray-700 flex items-center justify-center flex-shrink-0">
              {coachImage ? (
                <img
                  src={coachImage}
                  alt={coachName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gray-600 flex items-center justify-center">
                  <span className="text-gray-400 text-xl md:text-2xl font-bold">
                    {coachName?.charAt(0) || "C"}
                  </span>
                </div>
              )}
            </div>

            {/* Basic Info */}
            <div className="flex flex-col justify-center min-w-0">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-2 md:mb-3 text-white truncate">
                {coachName}
              </h1>
              <div className="space-y-1.5 md:space-y-2 text-sm md:text-base">
                <div className="flex gap-6 md:gap-10">
                  <span className="text-white/70 text-xs md:text-sm font-light">
                    Position:
                  </span>
                  <span className="text-xs md:text-sm font-light text-white/70">
                    Coach
                  </span>
                </div>
                <div className="flex gap-6 md:gap-10">
                  <span className="text-white/70 text-xs md:text-sm font-light">
                    Age:
                  </span>
                  <span className="text-xs md:text-sm font-light text-white/70">
                    {coachAge ? `${coachAge} yrs` : "-"}
                  </span>
                </div>
                {joinedText && (
                  <div className="flex gap-6 md:gap-10">
                    <span className="text-white/70 text-xs md:text-sm font-light">
                      Joined:
                    </span>
                    <span className="text-xs md:text-sm font-light text-white/70">
                      {joinedText}
                    </span>
                  </div>
                )}
                {untilText && (
                  <div className="flex gap-6 md:gap-10">
                    <span className="text-white/70 text-xs md:text-sm font-light">
                      Contract until:
                    </span>
                    <span className="text-xs md:text-sm font-light text-white/70">
                      {untilText}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Middle Section - Key Details Cards */}
          {(coach?.team || coach?.preferred_formation || coach?.agency || coach?.agent) && (
            <div className="flex flex-col gap-3 md:gap-4 ml-0 md:ml-auto w-full md:w-auto">
              <div className="flex items-center gap-3 md:gap-4 flex-wrap">
                {/* Team Card - Only show if team exists. Clickable → club
                    profile when the platform club id is known (Marin
                    2026-08-21); plain card otherwise. */}
                {coach?.team && (
                  <div
                    className={`bg-white backdrop-blur-sm rounded-xl px-4 md:px-6 py-3 md:py-4 min-w-[140px] flex-1 md:flex-none ${
                      coach.team.platform_club_id
                        ? "cursor-pointer hover:bg-gray-50"
                        : ""
                    }`}
                    role={coach.team.platform_club_id ? "button" : undefined}
                    tabIndex={coach.team.platform_club_id ? 0 : undefined}
                    onClick={
                      coach.team.platform_club_id
                        ? (e) => {
                            e.stopPropagation();
                            navigate(`/search/club/${coach.team.platform_club_id}`);
                          }
                        : undefined
                    }
                    onKeyDown={
                      coach.team.platform_club_id
                        ? (e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              e.stopPropagation();
                              navigate(`/search/club/${coach.team.platform_club_id}`);
                            }
                          }
                        : undefined
                    }
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xs md:text-sm text-[#1A212F]">Team:</span>
                      <div className="flex items-center gap-2 min-w-0">
                        {teamInfo.image && (
                          <img
                            src={teamInfo.image}
                            alt={teamInfo.name}
                            className="w-5 h-5 md:w-6 md:h-6 object-contain rounded-full flex-shrink-0"
                          />
                        )}
                        <span className="text-xs md:text-sm font-bold text-[#1A212F] truncate">
                          {teamInfo.name}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Formation Card */}
                {coach?.preferred_formation && (
                  <div className="bg-white backdrop-blur-sm rounded-xl px-4 md:px-6 py-3 md:py-4 min-w-[140px] md:min-w-[160px] flex-1 md:flex-none">
                    <div className="flex items-center gap-2">
                      <span className="text-xs md:text-sm text-[#1A212F]">Formation:</span>
                      <span className="text-xs md:text-sm font-bold text-[#1A212F]">
                        {coach.preferred_formation}
                      </span>
                    </div>
                  </div>
                )}

                {/* Contract data moved next to Position/Age as quiet label
                    rows (Marin 2026-08-21) — no card here anymore. */}

                {/* Agency/Agent Card (BE PR #447, Marins Fund) — analogue of
                    the player profile's agent block: active mandate only,
                    null renders nothing. Click → agency profile (or agent
                    profile when the agent has no agency). */}
                {(coach?.agency || coach?.agent) && (
                  <div
                    className="bg-white backdrop-blur-sm rounded-xl px-4 md:px-6 py-3 md:py-4 min-w-[140px] flex-1 md:flex-none cursor-pointer hover:bg-gray-50"
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (coach?.agency?.agency_id) {
                        navigate(`/search/agency/${coach.agency.agency_id}`);
                      } else if (coach?.agent?.account_id) {
                        navigate(`/search/agent/${coach.agent.account_id}`);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        e.stopPropagation();
                        if (coach?.agency?.agency_id) {
                          navigate(`/search/agency/${coach.agency.agency_id}`);
                        } else if (coach?.agent?.account_id) {
                          navigate(`/search/agent/${coach.agent.account_id}`);
                        }
                      }
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xs md:text-sm text-[#1A212F]">
                        {coach?.agency ? "Agency:" : "Agent:"}
                      </span>
                      <div className="flex items-center gap-2 min-w-0">
                        {(coach?.agency?.profile_picture_url ||
                          (!coach?.agency && coach?.agent?.profile_picture_url)) && (
                          <img
                            src={
                              coach?.agency?.profile_picture_url ||
                              coach?.agent?.profile_picture_url
                            }
                            alt=""
                            className="w-5 h-5 md:w-6 md:h-6 object-cover rounded-full flex-shrink-0"
                          />
                        )}
                        <span className="text-xs md:text-sm font-bold text-[#1A212F] truncate">
                          {coach?.agency?.name || coach?.agent?.name}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Country Flag — desktop: pinned bottom-right; MOBILE: in normal flow
          below the stacked content (the absolute pin collided with the
          stacking content inside the fixed-height hero — Marins phone
          finding 2026-08-21). */}
      {nationality && (
        <div className="static self-end mx-4 mb-3 mt-1 md:m-0 md:absolute md:right-6 md:bottom-6 flex items-center gap-2 md:gap-3">
          {nationality.image_url || nationality.logo ? (
            <img
              src={nationality.image_url || nationality.logo}
              alt={nationality.name || nationality}
              className="h-5 w-7 md:h-6 md:w-8 object-cover rounded-sm border border-white/20"
            />
          ) : (
            <div className="h-5 w-7 md:h-6 md:w-8 bg-gradient-to-r from-blue-500 via-white to-red-500 rounded-sm flex items-center justify-center border border-white/20">
              <span className="text-[10px] md:text-xs font-bold text-black">
                {nationality.code || (typeof nationality === 'string' ? nationality.substring(0, 2).toUpperCase() : nationality.name?.substring(0, 2).toUpperCase()) || "N/A"}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CoachHeroBanner;

